import React, { useState, useEffect, useCallback } from 'react';
import { TrackConfig } from './types';
import { useColumnSettings } from './hooks/useColumnSettings';
import { useControlsVisible } from './hooks/useControlsVisible';
import { useTouchUi } from './hooks/useTouchUi';
import { useHighlightedTeams } from './hooks/useHighlightedTeams';
import { TrackSelector } from './components/TrackSelector';
import { TeamGrid } from './components/TeamGrid';
import { ConnectionStatus } from './components/ConnectionStatus';
import { ControlPanel } from './components/ControlPanel';
import { LogPanel } from './components/LogPanel';
import { RaceControlPanel } from './components/RaceControlPanel';
import { PenaltyModal } from './components/PenaltyModal';
import { SessionTimer } from './components/SessionTimer';
import { PitWindow } from './components/PitWindow';
import { StrategyPanel } from './components/StrategyPanel';
import { ChartsPanel } from './components/ChartsPanel';
import { SectorsPanel } from './components/SectorsPanel';
import { DriverPlanPanel } from './components/DriverPlanPanel';
import { LiveStrategyPanel, StopPrompts } from './components/LiveStrategyPanel';
import { AlertPanel } from './components/AlertPanel';
import { useAlerts } from './alerts/useAlerts';
import { useStrategy } from './hooks/useStrategy';
import { useLiveStrategy } from './hooks/useLiveStrategy';
import { TeamStintsModal } from './components/TeamStintsModal';
import { useTimingSocket } from './useTimingSocket';
import { EventsList } from './components/EventsList';
import { EventDetail } from './components/EventDetail';
import { ReplayView } from './components/ReplayView';
import { AdminUsers } from './components/AdminUsers';
import { PitBoardPage } from './components/PitBoardPage';
import { StrategyPage } from './components/StrategyPage';
import { RankingsPage } from './components/RankingsPage';
import { DriverDetailPage } from './components/DriverDetailPage';
import { LoginScreen } from './components/LoginScreen';
import { SessionBadge } from './components/SessionBadge';
import { useAuth } from './auth/AuthContext';
import './App.css';

const DEFAULT_TRACK_NAME = 'Karting Events Bulgaria';

function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function getTrackSlugFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('track');
}

function setTrackSlugInUrl(track: TrackConfig | null): void {
  const url = new URL(window.location.href);
  if (track) {
    url.searchParams.set('track', slugify(track.name));
  } else {
    url.searchParams.delete('track');
  }
  window.history.replaceState(null, '', url.toString());
}

function usePathname(): string {
  const [pathname, setPathname] = useState<string>(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  );
  useEffect(() => {
    const onPop = (): void => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    const onNav = (): void => setPathname(window.location.pathname);
    window.addEventListener('karting:navigate', onNav);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('karting:navigate', onNav);
    };
  }, []);
  return pathname;
}

const App: React.FC = () => {
  const pathname = usePathname();
  const { status, isAdmin } = useAuth();

  // Above the route dispatch below: every branch returns early, and the touch
  // tier has to be set for all of them.
  useTouchUi();

  // Nothing in this app is public — hold everything until we know who is asking.
  if (status === 'loading') {
    return (
      <div className="App">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  if (status === 'anonymous') {
    return <LoginScreen />;
  }

  if (pathname === '/events') {
    return <EventsList />;
  }

  if (pathname === '/pit') {
    return <PitBoardPage />;
  }

  if (pathname === '/strategy') {
    return <StrategyPage />;
  }

  if (pathname === '/rankings') {
    return <RankingsPage />;
  }

  const driverMatch = pathname.match(/^\/rankings\/([^/]+)$/);
  if (driverMatch) {
    return <DriverDetailPage driverId={driverMatch[1]} />;
  }

  if (pathname === '/admin') {
    // Guarded on the server too; this just gives a viewer who typed the URL a
    // straight answer instead of a screen full of failed requests.
    return isAdmin ? <AdminUsers /> : <AdminOnlyNotice />;
  }

  const replayMatch = pathname.match(/^\/events\/([^/]+)\/replay$/);
  if (replayMatch) {
    return <ReplayView eventId={replayMatch[1]} />;
  }

  const eventMatch = pathname.match(/^\/events\/([^/]+)$/);
  if (eventMatch) {
    return <EventDetail eventId={eventMatch[1]} />;
  }

  return <LiveApp />;
};

const AdminOnlyNotice: React.FC = () => (
  <div className="App">
    <div className="app-header">
      <h1>Karting Live Timing</h1>
    </div>
    <div className="error">
      Account management is available to administrators only.{' '}
      <button
        className="events-link-button"
        onClick={() => {
          window.history.pushState(null, '', '/');
          window.dispatchEvent(new Event('karting:navigate'));
        }}
      >
        Back to live
      </button>
    </div>
  </div>
);

const LiveApp: React.FC = () => {
  const [tracks, setTracks] = useState<TrackConfig[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<TrackConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLogsVisible, setIsLogsVisible] = useState(false);
  const [isRaceControlVisible, setIsRaceControlVisible] = useState(false);
  const [isStrategyVisible, setIsStrategyVisible] = useState(false);
  const [isChartsVisible, setIsChartsVisible] = useState(false);
  const [isSectorsVisible, setIsSectorsVisible] = useState(false);
  const [isPlanVisible, setIsPlanVisible] = useState(false);
  const [isAlertsVisible, setIsAlertsVisible] = useState(false);
  const [isStintPlanVisible, setIsStintPlanVisible] = useState(false);
  const { isAdmin } = useAuth();
  const { areControlsVisible, toggleControls } = useControlsVisible();
  const { highlightedTeamIds, toggleHighlight: handleToggleHighlight } =
    useHighlightedTeams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [stintsDetailTeamId, setStintsDetailTeamId] = useState<string | null>(null);
  const [penaltyTeamId, setPenaltyTeamId] = useState<string | null>(null);
  const { columns, toggleColumn, resetColumns, getVisibleColumns, showAllColumns, hideAllOptionalColumns } = useColumnSettings();


  const {
    isSocketConnected,
    isApexConnected,
    teams,
    messages,
    raceControl,
    sessionTimer,
    raceRules,
    racePlan,
    activeEvent,
    lapHistory,
    sectorHistory,
    sectorHistoryVersion,
    loadSectorBackfill,
    strategyVersion,
    startListening,
    stopListening,
    setSortMode,
    sortMode,
  } = useTimingSocket();

  /**
   * Strategy is admin-only, but computing it unconditionally keeps the pit-stop
   * store folding every frame — a viewer who is later promoted would otherwise
   * start with no stop history at all.
   */
  const strategy = useStrategy(teams, {
    rules: raceRules,
    sessionEndsAt: sessionTimer?.endsAt ?? null,
    ourTeamIds: highlightedTeamIds,
  });

  /**
   * The stint plans attached to this race. Open to anyone who can see a strategy,
   * not just admins: the server decides which plans each account gets back.
   */
  const liveStrategy = useLiveStrategy({
    teams,
    eventId: activeEvent?.id ?? null,
    sessionEndsAt: sessionTimer?.endsAt ?? null,
    strategyVersion,
  });
  const hasLivePlans = liveStrategy.byTeam.size > 0;

  /** Count of penalty/warning entries per kart number, for the per-row ⚠ badge. */
  const penaltyCountByKart = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const entry of raceControl) {
      if (!entry.kartNumber) continue;
      if (entry.flag !== 'penalty' && entry.flag !== 'warning') continue;
      counts.set(entry.kartNumber, (counts.get(entry.kartNumber) ?? 0) + 1);
    }
    return counts;
  }, [raceControl]);

  /**
   * Alerts run only for admins and only on the live feed — the replay screen
   * does not mount them at all, since a 60× replay would fire hundreds.
   */
  const noTeams = React.useMemo(() => new Set<string>(), []);
  const alerts = useAlerts({
    teams,
    // A non-admin with a plan hears about that plan, not about every kart they
    // happen to have starred.
    watchedTeamIds: isAdmin ? highlightedTeamIds : noTeams,
    rules: raceRules,
    penaltyCountByKart,
    minutesRemaining: strategy.minutesRemaining,
    livePlans: liveStrategy.byTeam,
    active: isAdmin || hasLivePlans,
  });

  /** Selected team for the penalty-reasons popup, and that team's entries. */
  const penaltyTeam = penaltyTeamId ? teams.get(penaltyTeamId) ?? null : null;
  const penaltyEntries = React.useMemo(() => {
    if (!penaltyTeam) return [];
    return raceControl.filter(
      (e) =>
        e.kartNumber === penaltyTeam.kartNumber &&
        (e.flag === 'penalty' || e.flag === 'warning'),
    );
  }, [penaltyTeam, raceControl]);

  // Load config, then auto-select track from URL (or default)
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/config.json');
        if (!response.ok) throw new Error('Failed to load configuration');
        const configData = await response.json();
        const loadedTracks: TrackConfig[] = configData.tracks;
        setTracks(loadedTracks);

        const slug = getTrackSlugFromUrl();
        const match =
          loadedTracks.find((t) => slugify(t.name) === slug) ??
          loadedTracks.find((t) => t.name === DEFAULT_TRACK_NAME) ??
          loadedTracks[0];

        if (match) {
          setSelectedTrack(match);
          setTrackSlugInUrl(match);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleTrackSelect = useCallback((track: TrackConfig) => {
    stopListening();
    setSelectedTrack(track);
    setTrackSlugInUrl(track);
  }, [stopListening]);

  const handleStart = useCallback(() => {
    if (!selectedTrack) return;
    const raw = window.prompt('Event title');
    if (raw === null) return;
    const title = raw.trim();
    if (!title) return;
    startListening(selectedTrack, title);
  }, [selectedTrack, startListening]);

  const handleStop = useCallback(() => {
    stopListening();
  }, [stopListening]);

  const handleBackToTracks = useCallback(() => {
    stopListening();
    setSelectedTrack(null);
    setTrackSlugInUrl(null);
  }, [stopListening]);


  if (loading) {
    return (
      <div className="App">
        <div className="app-header"><h1>Karting Live Timing</h1></div>
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="app-header"><h1>Karting Live Timing</h1></div>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionTimer endsAt={sessionTimer?.endsAt ?? null} />
        <ConnectionStatus isConnected={isApexConnected} />
        {isAdmin && (
          <button
            className="header-toggle"
            type="button"
            onClick={toggleControls}
            aria-pressed={areControlsVisible}
            title={
              areControlsVisible
                ? 'Hide the Start/Stop bar'
                : 'Show the Start/Stop bar'
            }
          >
            {areControlsVisible ? 'Hide controls' : 'Show controls'}
          </button>
        )}
        <SessionBadge />
      </div>

      {!selectedTrack ? (
        <TrackSelector tracks={tracks} onTrackSelect={handleTrackSelect} />
      ) : (
        <div className="app-main">
          <div className="main-content">
            <ControlPanel
              selectedTrack={selectedTrack}
              onStart={handleStart}
              onStop={handleStop}
              onBackToTracks={handleBackToTracks}
              onSortModeChange={setSortMode}
              sortMode={sortMode}
              onToggleLogs={() => setIsLogsVisible(v => !v)}
              isLogsVisible={isLogsVisible}
              onToggleRaceControl={() => setIsRaceControlVisible(v => !v)}
              isRaceControlVisible={isRaceControlVisible}
              onToggleStrategy={() => setIsStrategyVisible(v => !v)}
              isStrategyVisible={isStrategyVisible}
              onToggleCharts={() => setIsChartsVisible(v => !v)}
              isChartsVisible={isChartsVisible}
              onToggleSectors={() => setIsSectorsVisible(v => !v)}
              isSectorsVisible={isSectorsVisible}
              onTogglePlan={() => setIsPlanVisible(v => !v)}
              isPlanVisible={isPlanVisible}
              onToggleAlerts={() => setIsAlertsVisible(v => !v)}
              isAlertsVisible={isAlertsVisible}
              alertCount={alerts.alerts.length}
              onToggleStintPlan={() => setIsStintPlanVisible(v => !v)}
              isStintPlanVisible={isStintPlanVisible}
              stintPlanCount={liveStrategy.plans.length}
              canSeeAlerts={isAdmin || hasLivePlans}
              raceControlCount={raceControl.length}
              isSocketConnected={isSocketConnected}
              isApexConnected={isApexConnected}
              columns={columns}
              onToggleColumn={toggleColumn}
              onResetColumns={resetColumns}
              onShowAllColumns={showAllColumns}
              onHideOptionalColumns={hideAllOptionalColumns}
              isAdmin={isAdmin}
              showConnectionControls={isAdmin && areControlsVisible}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />

            <PitWindow teams={teams} rules={raceRules} />

            <StopPrompts
              plans={liveStrategy.plans}
              teams={teams}
              dueSoonMinutes={raceRules.dueSoonMinutes}
            />

            {(isAdmin || hasLivePlans) && isAlertsVisible && <AlertPanel {...alerts} />}

            {isStintPlanVisible && (
              <LiveStrategyPanel
                live={liveStrategy}
                teams={teams}
                eventId={activeEvent?.id ?? null}
                dueSoonMinutes={raceRules.dueSoonMinutes}
              />
            )}

            {isAdmin && isStrategyVisible && (
              <StrategyPanel summary={strategy} ourTeamIds={highlightedTeamIds} />
            )}

            {isAdmin && isChartsVisible && (
              <ChartsPanel
                teams={teams}
                lapHistory={lapHistory}
                ourTeamIds={highlightedTeamIds}
              />
            )}

            {isAdmin && isSectorsVisible && (
              <SectorsPanel
                teams={teams}
                history={sectorHistory}
                version={sectorHistoryVersion}
                ourTeamIds={highlightedTeamIds}
                loadEarlier={loadSectorBackfill}
              />
            )}

            {isAdmin && isPlanVisible && (
              <DriverPlanPanel
                teams={teams}
                ourTeamIds={highlightedTeamIds}
                rules={raceRules}
                minutesRemaining={strategy.minutesRemaining}
                plan={racePlan}
                activeEvent={activeEvent}
              />
            )}

            <TeamGrid
              teams={teams}
              sortMode={sortMode}
              highlightedTeamIds={highlightedTeamIds}
              onToggleHighlight={handleToggleHighlight}
              onOpenTeamDetail={setStintsDetailTeamId}
              visibleColumns={getVisibleColumns()}
              searchQuery={searchQuery}
              penaltyCountByKart={penaltyCountByKart}
              onOpenPenalties={setPenaltyTeamId}
            />

            <TeamStintsModal
              team={
                stintsDetailTeamId ? teams.get(stintsDetailTeamId) ?? null : null
              }
              lapHistory={lapHistory}
              onClose={() => setStintsDetailTeamId(null)}
            />

            <PenaltyModal
              team={penaltyTeam}
              entries={penaltyEntries}
              onClose={() => setPenaltyTeamId(null)}
            />

            {isRaceControlVisible && <RaceControlPanel entries={raceControl} />}

            {isLogsVisible && <LogPanel messages={messages} />}
          </div>
        </div>
      )}

      {/* Track picker — always accessible via the "Change Track" button */}
      {selectedTrack && (
        <div style={{ display: 'none' }}>
          <TrackSelector tracks={tracks} onTrackSelect={handleTrackSelect} />
        </div>
      )}
    </div>
  );
};

export default App;
